import { Form, message, Row, Col, Space } from "antd";
import React, { useEffect, useState } from "react";
import { useLeaveStore } from "../../../../config/stores";
import { useSelector } from "react-redux";
import AppButton from "../../../../components/common/AppButton";
import AppInput from "../../../../components/common/AppInput";
import { AiOutlineClose, AiOutlineSave } from "react-icons/ai";
import dayjs from "dayjs";
import { isEmpty } from "lodash";
import AntdModal from "../../../../components/common/AntdModal";
import axios from "axios";

const isWeekend = (date) => {
  const day = dayjs(date).day();
  return day === 0 || day === 6;
};

const calculateWorkingDays = (start, end, holidays = []) => {
  const holidayDates = new Set(holidays.map((h) => dayjs(h.date).format("YYYY-MM-DD")));
  const totalCalendarDays = dayjs(end).diff(dayjs(start), "day") + 1;
  let count = 0;
  for (let i = 0; i < totalCalendarDays; i++) {
    const current = dayjs(start).add(i, "day");
    if (!isWeekend(current) && !holidayDates.has(current.format("YYYY-MM-DD"))) {
      count++;
    }
  }
  return count;
};

const LeaveApplicationForm = () => {
  const leaveStore = useLeaveStore();
  const { data: currentUserInfo } = useSelector(
    (state) => state.usersState.currentUserInfo
  );
  const [form] = Form.useForm();
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [publicHolidays, setPublicHolidays] = useState([]);
  const [handoverStaff, setHandoverStaff] = useState([]);
  const [selectedLeaveTypeDetails, setSelectedLeaveTypeDetails] = useState(null);
  const [userLeaveBalance, setUserLeaveBalance] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(false);
  
  const leaveType = Form.useWatch('leave_type', form);
  const startDate = Form.useWatch('start_date', form);
  const endDate = Form.useWatch('end_date', form);
  const numberOfDays = Form.useWatch('number_of_days', form);
  const totalLeaveDaysToBeTaken = Form.useWatch('total_leave_days_to_be_taken', form);
  const totalLeaveDaysAvailable = Form.useWatch('total_leave_days_available', form);

  useEffect(() => {
    const fetchLeaveTypes = async () => {
      try {
        const res = await axios.get("/settings/leave-types");
        let leaveTypesData = [];
        if (Array.isArray(res)) {
          leaveTypesData = res;
        } else if (Array.isArray(res?.data)) {
          leaveTypesData = res.data;
        } else if (res?.data?.data && Array.isArray(res.data.data)) {
          leaveTypesData = res.data.data;
        } else if (res?.data && typeof res.data === 'object') {
          leaveTypesData = res.data.data || [];
        }
        setLeaveTypes(leaveTypesData);
      } catch (error) {
        console.error("Failed to fetch leave types");
      }
    };
    fetchLeaveTypes();

    const fetchPublicHolidays = async () => {
      try {
        const res = await axios.get("/settings/public-holidays", { skipNotification: true });
        let holidaysData = [];
        if (Array.isArray(res)) {
          holidaysData = res;
        } else if (Array.isArray(res?.data)) {
          holidaysData = res.data;
        } else if (res?.data?.data && Array.isArray(res.data.data)) {
          holidaysData = res.data.data;
        } else if (res?.data && typeof res.data === 'object') {
          holidaysData = res.data.data || [];
        }
        setPublicHolidays(holidaysData);
      } catch (error) {
        console.error("Failed to fetch public holidays");
      }
    };
    fetchPublicHolidays();

    const fetchHandoverStaff = async () => {
      try {
        const res = await axios.get("/administration/leave-applications/handover-staff", { skipNotification: true });
        let staffData = [];
        if (Array.isArray(res)) {
          staffData = res;
        } else if (Array.isArray(res?.data)) {
          staffData = res.data;
        } else if (Array.isArray(res?.data?.data)) {
          staffData = res.data.data;
        }
        setHandoverStaff(staffData);
      } catch (error) {
        console.error("Failed to fetch staff");
      }
    };
    fetchHandoverStaff();
  }, []);

  const fetchUserLeaveBalance = async (leaveTypeId, leaveTypeName) => {
    if (!currentUserInfo?.id || !leaveTypeId) {
      setUserLeaveBalance(null);
      return;
    }
    
    setLoadingBalance(true);
    try {
      const applicantId = leaveStore.applicationToEdit?.applicant_id;
      const res = await axios.get(`/administration/leave-applications/my-balance`, {
        params: {
          leaveTypeId,
          ...(applicantId && applicantId !== currentUserInfo?.id ? { userId: applicantId } : {}),
        },
        skipNotification: true
      });
      
      let balanceData = null;
      if (res?.data) {
        balanceData = {
          accrued: res.data.accrued || res.data.total_leave_days_accrued || 0,
          available: res.data.available || res.data.total_leave_days_available || 0,
          used: res.data.used || 0,
        };
      } else if (res?.accrued !== undefined) {
        balanceData = {
          accrued: res.accrued || 0,
          available: res.available || 0,
          used: res.used || 0,
        };
      }
      
      if (balanceData) {
        setUserLeaveBalance(balanceData);
        form.setFieldValue('total_leave_days_accrued', balanceData.accrued);
        form.setFieldValue('total_leave_days_available', balanceData.available);
      } else {
        setUserLeaveBalance({
          accrued: 0,
          available: 0,
          used: 0,
        });
        form.setFieldValue('total_leave_days_accrued', 0);
        form.setFieldValue('total_leave_days_available', 0);
      }
    } catch (error) {
      console.error("Error fetching leave balance:", error);
      if (error?.response?.status === 404) {
        message.warning(
          `Leave balance endpoint not implemented yet. Please enter your balance manually for ${leaveTypeName || 'this leave type'}.`
        );
        setUserLeaveBalance(null);
      } else {
        setUserLeaveBalance({
          accrued: 0,
          available: 0,
          used: 0,
        });
        form.setFieldValue('total_leave_days_accrued', 0);
        form.setFieldValue('total_leave_days_available', 0);
      }
    } finally {
      setLoadingBalance(false);
    }
  };

  useEffect(() => {
    if (leaveType) {
      const leaveTypeDetail = leaveTypes.find(lt => lt.name === leaveType);
      setSelectedLeaveTypeDetails(leaveTypeDetail || null);
      
      if (leaveTypeDetail?.id) {
        fetchUserLeaveBalance(leaveTypeDetail.id, leaveTypeDetail.name);
      }
    } else {
      setSelectedLeaveTypeDetails(null);
      setUserLeaveBalance(null);
      form.setFieldValue('total_leave_days_accrued', null);
      form.setFieldValue('total_leave_days_available', null);
    }
  }, [leaveType, leaveTypes, form]);

  useEffect(() => {
    if (startDate && endDate) {
      const days = calculateWorkingDays(startDate, endDate, publicHolidays);
      form.setFieldValue('number_of_days', days);
      form.setFieldValue('total_leave_days_to_be_taken', days);
      
      if (selectedLeaveTypeDetails?.max_days && days > selectedLeaveTypeDetails.max_days) {
        message.error(
          `The selected date range (${days} days) exceeds the maximum allowed days (${selectedLeaveTypeDetails.max_days} days) for ${selectedLeaveTypeDetails.name}. Please adjust your dates.`
        );
      }
      
      if (userLeaveBalance?.available !== undefined && days > userLeaveBalance.available) {
        message.warning(
          `The selected date range (${days} days) exceeds your available leave balance (${userLeaveBalance.available} days).`
        );
      }
    } else if (!startDate || !endDate) {
      form.setFieldValue('number_of_days', null);
      form.setFieldValue('total_leave_days_to_be_taken', null);
    }
  }, [startDate, endDate, selectedLeaveTypeDetails, userLeaveBalance, publicHolidays, form]);

  useEffect(() => {
    if (totalLeaveDaysAvailable !== null && totalLeaveDaysAvailable !== undefined && 
        totalLeaveDaysToBeTaken !== null && totalLeaveDaysToBeTaken !== undefined) {
      const balance = Number(totalLeaveDaysAvailable) - Number(totalLeaveDaysToBeTaken);
      form.setFieldValue('balance_after_leave', balance);
    }
  }, [totalLeaveDaysAvailable, totalLeaveDaysToBeTaken, form]);


  useEffect(() => {
    if (!leaveStore.addingLeaveApplication) {
      form.resetFields();
    } else if (!isEmpty(leaveStore.applicationToEdit)) {
      const editing = leaveStore.applicationToEdit;
      const storedType = editing.leave_type === "OTHER" ? editing.other_leave_type : editing.leave_type;
      const matchedType = leaveTypes.find(
        (lt) =>
          lt.name?.toUpperCase() === String(storedType || "").toUpperCase() ||
          lt.name?.toUpperCase().startsWith(String(storedType || "").toUpperCase())
      );
      form.setFieldsValue({
        ...editing,
        leave_type: matchedType?.name ?? storedType,
        start_date: dayjs(editing.start_date),
        end_date: dayjs(editing.end_date),
      });
    }
  }, [leaveStore.addingLeaveApplication, leaveStore.applicationToEdit, leaveTypes, form]);

  const onSave = () => {
    form
      .validateFields()
      .then((values) => {
        const formData = {
          ...values,
          applicant_id: currentUserInfo?.id,
          status: isEmpty(leaveStore.applicationToEdit) ? "PENDING" : values.status,
        };

        if (isEmpty(leaveStore.applicationToEdit)) {
          leaveStore.createLeaveApplication(formData);
        } else {
          leaveStore.updateLeaveApplication(leaveStore.applicationToEdit.id, formData);
        }
      })
      .catch(() => {
        message.error("Please fill in all fields correctly");
      });
  };

  return (
    <AntdModal
      open={leaveStore.showAddLeaveForm}
      closeModal={() => leaveStore.setAddingLeaveApplication()}
      title={
        isEmpty(leaveStore.applicationToEdit)
          ? "NEW LEAVE APPLICATION"
          : "UPDATE LEAVE APPLICATION"
      }
      size="large"
      footer={
        <div className="d-flex justify-content-end gap-2">
          <AppButton
            type="default"
            onClick={() => leaveStore.setAddingLeaveApplication()}
            iconBefore={<AiOutlineClose />}
            text="Cancel"
          />
          <AppButton
            type="primary"
            onClick={onSave}
            iconBefore={<AiOutlineSave />}
            loading={leaveStore.creating || leaveStore.updating}
            text={isEmpty(leaveStore.applicationToEdit) ? "Submit" : "Update"}
          />
        </div>
      }
    >
      <Form
        form={form}
        layout="vertical"
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <AppInput
              type="select"
              name="leave_type"
              label="Leave Type"
              rules={[{ required: true, message: "Leave type is required" }]}
              options={leaveTypes.map(lt => ({
                label: lt.name,
                value: lt.name,
              }))}
              inputAttributes={{
                placeholder: "Select Leave Type",
                showSearch: true,
              }}
            />
          </Col>


          <Col xs={24} md={12}>
            <AppInput
              name="address_while_on_leave"
              label="Address While on Leave"
              rules={[{ required: true, message: "Address is required" }]}
              inputAttributes={{
                placeholder: "Enter Address",
              }}
            />
          </Col>

          <Col xs={24} md={12}>
            <AppInput
              name="phone_while_on_leave"
              label="Phone Number While on Leave"
              rules={[{ required: true, message: "Phone number is required" }]}
              inputAttributes={{
                placeholder: "Enter Phone Number",
              }}
            />
          </Col>

          <Col xs={24} md={12}>
            <AppInput
              type="select"
              name="handed_over_to_id"
              label="Handing Over To"
              rules={[{ required: true, message: "Please select the staff you are handing over to" }]}
              options={handoverStaff.map((staff) => ({
                label: `${staff.first_name ?? ""} ${staff.other_names ?? ""}`.trim(),
                value: staff.id,
              }))}
              inputAttributes={{
                placeholder: "Select Staff",
                showSearch: true,
                optionFilterProp: "label",
              }}
            />
          </Col>

          <Col xs={24} md={12}>
            <div className="ant-form-item-label">
              <label className="ant-form-item-required">Leave Period</label>
            </div>
            <Space.Compact style={{ width: "100%" }}>
              <AppInput
                type="date"
                name="start_date"
                rules={[
                  { required: true, message: "Start date is required" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const endDate = getFieldValue('end_date');
                      if (!value || !endDate) return Promise.resolve();
                      if (dayjs(value).isAfter(dayjs(endDate))) {
                        return Promise.reject(new Error('Start date cannot be after end date'));
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
                style={{ width: "50%" }}
                inputAttributes={{
                  placeholder: "Start Date",
                }}
              />
              <AppInput
                type="date"
                name="end_date"
                rules={[
                  { required: true, message: "End date is required" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const startDate = getFieldValue('start_date');
                      if (!value || !startDate) return Promise.resolve();
                      if (dayjs(value).isBefore(dayjs(startDate))) {
                        return Promise.reject(new Error('End date cannot be before start date'));
                      }
                      
                      const days = calculateWorkingDays(startDate, value, publicHolidays);
                      if (selectedLeaveTypeDetails?.max_days && days > selectedLeaveTypeDetails.max_days) {
                        return Promise.reject(
                          new Error(`Date range exceeds maximum of ${selectedLeaveTypeDetails.max_days} days for this leave type`)
                        );
                      }
                      
                      if (userLeaveBalance?.available !== undefined && days > userLeaveBalance.available) {
                        return Promise.reject(
                          new Error(`Date range exceeds available balance of ${userLeaveBalance.available} days`)
                        );
                      }
                      
                      return Promise.resolve();
                    },
                  }),
                ]}
                style={{ width: "50%" }}
                inputAttributes={{
                  placeholder: "End Date",
                }}
              />
            </Space.Compact>
          </Col>

          <Col xs={24} md={12}>
            <AppInput
              name="number_of_days"
              label="Number of Days"
              inputAttributes={{
                disabled: true,
              }}
            />
            <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '4px' }}>
              Excludes weekends and public holidays
            </div>
          </Col>

          <Col xs={24} md={12}>
            <AppInput
              name="total_leave_days_accrued"
              label="Total Leave Days Accrued"
              type="number"
              rules={[{ required: true, message: "Total leave days accrued is required" }]}
              inputAttributes={{
                placeholder: "Auto-filled from balance",
                disabled: loadingBalance,
              }}
            />
            {selectedLeaveTypeDetails && (
              <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '4px' }}>
                Your total accrued days for {selectedLeaveTypeDetails.name}
              </div>
            )}
          </Col>

          <Col xs={24} md={12}>
            <AppInput
              name="total_leave_days_available"
              label="Total Leave Days Available"
              type="number"
              rules={[{ required: true, message: "Total leave days available is required" }]}
              inputAttributes={{
                placeholder: "Auto-filled from balance",
                disabled: loadingBalance,
              }}
            />
            {selectedLeaveTypeDetails && (
              <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '4px' }}>
                {selectedLeaveTypeDetails.max_days 
                  ? `Available: ${totalLeaveDaysAvailable || 0} days (Max allowed: ${selectedLeaveTypeDetails.max_days} days)`
                  : `Available: ${totalLeaveDaysAvailable || 0} days`}
              </div>
            )}
          </Col>

          <Col xs={24} md={12}>
            <AppInput
              name="total_leave_days_to_be_taken"
              label="Total Leave Days To Be Taken"
              type="number"
              rules={[
                { required: true, message: "Total leave days to be taken is required" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value) return Promise.resolve();
                    const maxDays = selectedLeaveTypeDetails?.max_days;
                    if (maxDays && value > maxDays) {
                      return Promise.reject(new Error(`Cannot exceed maximum of ${maxDays} days for this leave type`));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
              inputAttributes={{
                placeholder: "Enter total leave days to be taken",
              }}
            />
          </Col>

          <Col xs={24} md={12}>
            <AppInput
              name="balance_after_leave"
              label="Balance After Leave"
              type="number"
              rules={[{ required: true, message: "Balance after leave is required" }]}
              inputAttributes={{
                placeholder: "Auto-calculated",
                disabled: true,
              }}
            />
          </Col>
        </Row>
      </Form>
    </AntdModal>
  );
};

export default LeaveApplicationForm;